import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GlobalTheme from "../../../styles/global.theme";
import * as Styled from "../../../CompanyForm.styled.js";
import * as MUIStyled from "../../../MUIStyled.js";
import { addCompanyDetail } from "../../../config/fetchAPI";
import { CustomTextField } from "../../elements/TextField/TextField";
import { CustomDropdownField } from "../../elements/Dropdown/Dropdown.js";
import { ButtonPurpleNext } from "../../elements/Buttons/Buttons.js";
import DynamicLogoUpload from "../../common/DynamicLogoUpload/DynamicLogoUpload";
import {
    buildAccountingSoftwareOptions,
    FALLBACK_OTHER_ACCOUNTING_SOFTWARE_ID,
    isOtherAccountingSoftware,
    validateCompanyDetails,
} from "../../../utils/companyDetailValidation";

const CompanyDetailForm = ({
    companyDetails,
    accountingSoftwareDropdown,
    companyTypesDropdown,
    industriesDropdown,
    payrollDaysOfMonthsDropdown,
    payrollFrequenciesDropdown,
}) => {
    const accountingSoftwareOptions = buildAccountingSoftwareOptions(
        accountingSoftwareDropdown
    );

    const [formData, setFormData] = useState({
        companyName: "",
        abn: "",
        tradingName: "",
        website: "",
        payrollFrequency: "",
        nextPayrollAt: "",
        payrollDayOfMonth1: "",
        payrollDayOfMonth2: "",
        otherAccountingSoftware: "",
        numberOfEmployees: "",
        employerMethodID: "",
        accountingSoftwareID: "",
        industryTypeID: "",
        companyTypeId: "",
    });

    useEffect(() => {
        if (companyDetails) {
            setFormData({
                companyName: companyDetails.companyName || "",
                abn: companyDetails.abn || "",
                tradingName: companyDetails.tradingName || "",
                website: companyDetails.website || "",
                payrollFrequency: companyDetails.payrollFrequency || "Monthly",
                nextPayrollAt: companyDetails.nextPayrollAt || "",
                payrollDayOfMonth1: companyDetails.payrollDayOfMonth1 || "",
                payrollDayOfMonth2: companyDetails.payrollDayOfMonth2 || "",
                otherAccountingSoftware:
                    companyDetails.otherAccountingSoftware || "",
                numberOfEmployees: companyDetails.numberOfEmployees || "",
                employerMethodID:
                    companyDetails.employerMethodID === null
                        ? "6"
                        : companyDetails.employerMethodID
                          ? String(companyDetails.employerMethodID)
                          : "6",
                accountingSoftwareID: companyDetails.accountingSoftwareID
                    ? String(companyDetails.accountingSoftwareID)
                    : "",
                industryTypeID: companyDetails.industryTypeID
                    ? String(companyDetails.industryTypeID)
                    : "",
                companyTypeId: companyDetails.companyTypeId
                    ? String(companyDetails.companyTypeId)
                    : "",
            });
        }
    }, [companyDetails]);

    const [isABNDisabled, setIsABNDisabled] = useState(true);

    const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState("");
    const [abnErrorMsg, setAbnErrorMsg] = useState("");
    const [websiteErrorMsg, setWebsiteErrorMsg] = useState("");
    const [companyTypeErrorMsg, setCompanyTypeErrorMsg] = useState("");
    const [noOfEmployeeErrorMsg, setNoOfEmployeeErrorMsg] = useState("");
    const [payrollFrequencyErrorMsg, setPayrollFrequencyErrorMsg] =
        useState("");
    const [nextPayrollAtErrorMsg, setNextPayrollAtErrorMsg] = useState("");
    const [payrollDayOfMonth1ErrorMsg, setPayrollDayOfMonth1ErrorMsg] =
        useState("");
    const [payrollDayOfMonth2ErrorMsg, setPayrollDayOfMonth2ErrorMsg] =
        useState("");
    const [accountingSoftwareErrorMsg, setAccountingSoftwareErrorMsg] =
        useState("");
    const [otherAccountingSoftwareMsg, setOtherAccountingSoftwareMsg] =
        useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingS3State, setIsLoadingS3State] = useState(false);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [serverError, setServerError] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        // enable field if stored ABN is incorrect
        const abn = companyDetails?.abn || "";
        if (abn.length !== 11 || isNaN(abn)) {
            setIsABNDisabled(false);
        }
        // enable field if an error occurs
        if (abnErrorMsg !== "") {
            setIsABNDisabled(false);
        }

        setIsABNDisabled(true);
    }, [abnErrorMsg, companyDetails]);

    const validateForm = () => {
        setCompanyNameErrorMsg("");
        setAbnErrorMsg("");
        setWebsiteErrorMsg("");
        setCompanyTypeErrorMsg("");
        setNoOfEmployeeErrorMsg("");
        setPayrollFrequencyErrorMsg("");
        setNextPayrollAtErrorMsg("");
        setPayrollDayOfMonth1ErrorMsg("");
        setPayrollDayOfMonth2ErrorMsg("");
        setAccountingSoftwareErrorMsg("");
        setOtherAccountingSoftwareMsg("");

        const errors = validateCompanyDetails(
            formData,
            accountingSoftwareDropdown
        );
        setCompanyNameErrorMsg(errors.companyName || "");
        setAbnErrorMsg(errors.abn || "");
        setCompanyTypeErrorMsg(errors.companyTypeId || "");
        setNoOfEmployeeErrorMsg(errors.numberOfEmployees || "");
        setNextPayrollAtErrorMsg(errors.nextPayrollAt || "");
        setPayrollDayOfMonth1ErrorMsg(errors.payrollDayOfMonth1 || "");
        setPayrollDayOfMonth2ErrorMsg(errors.payrollDayOfMonth2 || "");
        setAccountingSoftwareErrorMsg(errors.accountingSoftwareID || "");
        setOtherAccountingSoftwareMsg(errors.otherAccountingSoftware || "");

        return Object.keys(errors).length === 0;
    };

    const handlePayrollFrequencyChange = (event) => {
        const value = event.target.value;
        setFormData((prevState) => ({
            ...prevState,
            payrollFrequency: value,
        }));
    };

    const handleNextPayrollAtChange = (event) => {
        const { value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            nextPayrollAt: value,
        }));
    };

    const handlePayrollDayOfMonth1Change = (event) => {
        setPayrollDayOfMonth1ErrorMsg("");
        const value = event.target.value;
        setFormData((prevState) => ({
            ...prevState,
            payrollDayOfMonth1: value,
        }));
    };

    const handlePayrollDayOfMonth2Change = (event) => {
        setPayrollDayOfMonth2ErrorMsg("");
        const value = event.target.value;
        setFormData((prevState) => ({
            ...prevState,
            payrollDayOfMonth2: value,
        }));
    };

    const handleOtherAccountingSoftwareChange = (event) => {
        setOtherAccountingSoftwareMsg("");
        const value = event.target.value;
        setFormData((prevState) => ({
            ...prevState,
            otherAccountingSoftware: value,
        }));
    };

    const handleAccountingSoftwareChange = (event) => {
        setAccountingSoftwareErrorMsg("");
        const value = event.target.value;
        setFormData((prevState) => ({
            ...prevState,
            accountingSoftwareID: value,
        }));
    };

    const handleIndustryTypeChange = (event) => {
        const value = event.target.value;
        setFormData((prevState) => ({
            ...prevState,
            industryTypeID: value,
        }));
    };

    const handleCompanyTypeChange = (event) => {
        setCompanyTypeErrorMsg("");
        const value = event.target.value;
        setFormData((prevState) => ({
            ...prevState,
            companyTypeId: value,
        }));
    };

    const handleCompanyNameChange = (event) => {
        setCompanyNameErrorMsg("");
        setFormData((prevState) => ({
            ...prevState,
            companyName: event.target.value,
        }));
    };

    const handleABNChange = (event) => {
        setFormData((prevState) => ({
            ...prevState,
            abn: event.target.value,
        }));
    };

    const handleTradingNameChange = (event) => {
        setFormData((prevState) => ({
            ...prevState,
            tradingName: event.target.value,
        }));
    };

    const handleWebsiteChange = (event) => {
        setFormData((prevState) => ({
            ...prevState,
            website: event.target.value,
        }));
    };

    const handleNumberOfEmployeesChange = (event) => {
        let value = event.target.value;

        setNoOfEmployeeErrorMsg("");
        setFormData((prevState) => ({
            ...prevState,
            numberOfEmployees: value,
        }));

        const numValue = Number(value);
        if (isNaN(numValue)) {
            setNoOfEmployeeErrorMsg("Please enter a valid number.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsLoading(true);
            setServerError(null);
            setErrorMessage(null);
            setHasErrorOccurred(false);

            const result = await postCompanyDetails(formData);

            // Check if the result contains errors
            if (result && result.errors) {
                // Format errors into a readable message
                const errorMessages = Object.entries(result.errors)
                    .map(([field, messages]) => {
                        return `${messages.join(", ")}`;
                    })
                    .join(" | ");

                setServerError(errorMessages);
                setHasErrorOccurred(true);
            } else {
                // Success - navigate to next page
                navigate("/trading");
            }
        } catch (error) {
            setErrorMessage(
                "An error occurred during submission. Please try again."
            );
            setHasErrorOccurred(true);
            setServerError(
                "An unexpected error occurred. Please try again or contact support."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const postCompanyDetails = async () => {
        // Prepare form data to send to API
        // remove accountingSoftwareID if otherAccountingSoftware is selected
        if (
            formData.accountingSoftwareID ===
            FALLBACK_OTHER_ACCOUNTING_SOFTWARE_ID
        ) {
            formData.accountingSoftwareID = null;
        } else if (
            !isOtherAccountingSoftware(
                formData.accountingSoftwareID,
                accountingSoftwareDropdown
            )
        ) {
            formData.otherAccountingSoftware = null;
        }

        const apiPayload = {
            companyName: formData.companyName,
            abn: formData.abn,
            tradingName: formData.tradingName || null,
            website: formData.website || null,
            payrollFrequency: formData.payrollFrequency || null,
            nextPayrollAt: formData.nextPayrollAt || null,
            payrollDayOfMonth1: formData.payrollDayOfMonth1 || null,
            payrollDayOfMonth2: formData.payrollDayOfMonth2 || null,
            otherAccountingSoftware: formData.otherAccountingSoftware || null,
            numberOfEmployees: parseInt(formData.numberOfEmployees, 10) || null,
            employerMethodID: parseInt(formData.employerMethodID, 10) || null,
            accountingSoftwareID:
                parseInt(formData.accountingSoftwareID, 10) || null,
            industryTypeID: parseInt(formData.industryTypeID, 10) || null,
            companyTypeId: parseInt(formData.companyTypeId, 10) || null,
        };

        const result = await addCompanyDetail(apiPayload);
        return result;
    };

    // Function to render fields based on payrollFrequency
    const renderPayrollFields = () => {
        switch (formData.payrollFrequency) {
            case "Weekly":
            case "Fortnightly":
            case "Monthly":
                return (
                    <>
                        <MUIStyled.CustomGrid
                            size={{ xs: 12, sm: 6 }}
                            sx={{ marginTop: { xs: "10px", sm: "10px" } }}>
                            <CustomTextField
                                fullWidth
                                value={formData.nextPayrollAt}
                                onChange={handleNextPayrollAtChange}
                                label="Next payroll date*"
                                variant="outlined"
                                type="date"
                                slotProps={{
                                    inputLabel: {
                                        shrink: true,
                                    },
                                }}
                                sx={{
                                    marginBottom: "12px",
                                    "& .MuiInputLabel-root": {
                                        fontSize: "20px",
                                    },
                                }}
                                error={nextPayrollAtErrorMsg !== ""}
                                helperText={nextPayrollAtErrorMsg}
                            />
                        </MUIStyled.CustomGrid>
                    </>
                );
            case "Bi-Monthly":
                return (
                    <>
                        <MUIStyled.CustomGrid
                            size={{ xs: 12, sm: 6 }}></MUIStyled.CustomGrid>
                        <MUIStyled.CustomGrid
                            size={{ xs: 12, sm: 6 }}
                            sx={{ marginTop: { xs: "-12px", sm: "10px" } }}>
                            <CustomDropdownField
                                value={formData.payrollDayOfMonth1}
                                onChange={handlePayrollDayOfMonth1Change}
                                label="Select 1st payroll run day*"
                                fullWidth
                                variant="outlined"
                                error={payrollDayOfMonth1ErrorMsg !== ""}
                                helperText={payrollDayOfMonth1ErrorMsg}
                                options={[
                                    ...(payrollDaysOfMonthsDropdown
                                        ? payrollDaysOfMonthsDropdown.map(
                                              (day) => ({
                                                  value: day,
                                                  label: day,
                                              })
                                          )
                                        : []),
                                ]}
                            />
                        </MUIStyled.CustomGrid>
                        <MUIStyled.CustomGrid
                            size={{ xs: 12, sm: 6 }}
                            sx={{
                                marginTop: { xs: "10px", sm: "10px" },
                                marginBottom: { xs: "10px", sm: 0 },
                            }}>
                            <CustomDropdownField
                                value={formData.payrollDayOfMonth2}
                                onChange={handlePayrollDayOfMonth2Change}
                                label="Select 2nd payroll run day*"
                                fullWidth
                                variant="outlined"
                                error={payrollDayOfMonth2ErrorMsg !== ""}
                                helperText={payrollDayOfMonth2ErrorMsg}
                                options={[
                                    ...(payrollDaysOfMonthsDropdown
                                        ? payrollDaysOfMonthsDropdown.map(
                                              (day) => ({
                                                  value: day,
                                                  label: day,
                                              })
                                          )
                                        : []),
                                ]}
                            />
                        </MUIStyled.CustomGrid>
                    </>
                );

            case "Other":
                return (
                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}></MUIStyled.CustomGrid>
                );
            default:
                return null;
        }
    };

    // disable next button while loading
    const handleImageUploaded = (logoURL, isLoadingS3) => {
        setIsLoadingS3State(isLoadingS3);
    };

    return (
        <Styled.CompanyFormMainWrapper>
            <form
                onSubmit={handleSubmit}
                style={{ paddingBottom: 30, padding: 0, paddingTop: 30 }}>
                <MUIStyled.CustomGrid
                    container
                    spacing={4}
                    xs={10}
                    sx={{
                        marginTop: 1,
                        padding: {
                            xs: "5px",
                            sm: "50px",
                        },
                        paddingBottom: 1,
                        width: "100%",
                        margin: "0 auto",
                        height: "auto",
                        backgroundColor: `${GlobalTheme.palette.white.primary.main} !important`,
                        borderRadius: "8px",
                        border: `1px solid ${GlobalTheme.palette.grey.primary.main}`,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.06)",
                    }}>
                    {/* Company Info Section */}
                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{ marginTop: { xs: "50px", sm: "20px" } }}>
                        <CustomTextField
                            value={formData.companyName}
                            onChange={handleCompanyNameChange}
                            label="Company name*"
                            fullWidth
                            variant="outlined"
                            error={companyNameErrorMsg !== ""}
                            helperText={companyNameErrorMsg}
                            disabled={true}
                        />
                    </MUIStyled.CustomGrid>
                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{ marginTop: { xs: "10px", sm: "20px" } }}>
                        <CustomTextField
                            value={formData.abn}
                            onChange={handleABNChange}
                            label="ABN*"
                            fullWidth
                            variant="outlined"
                            error={abnErrorMsg !== ""}
                            helperText={abnErrorMsg}
                            sx={{
                                "& .MuiFormHelperText-root": {
                                    color: GlobalTheme.palette.purple.main,
                                    fontSize: "12px",
                                },
                            }}
                            disabled={isABNDisabled}
                        />
                    </MUIStyled.CustomGrid>
                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{ marginTop: { xs: "10px", sm: "15px" } }}>
                        <CustomTextField
                            value={formData.tradingName}
                            onChange={handleTradingNameChange}
                            label="Trading Name"
                            fullWidth
                            variant="outlined"
                        />
                    </MUIStyled.CustomGrid>
                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{ marginTop: { xs: "10px", sm: "15px" } }}>
                        <CustomTextField
                            value={formData.website}
                            onChange={handleWebsiteChange}
                            label="Website URL"
                            fullWidth
                            variant="outlined"
                            placeholder="www.example.com"
                            error={websiteErrorMsg !== ""}
                            helperText={websiteErrorMsg}
                        />
                    </MUIStyled.CustomGrid>
                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{
                            display: "flex",
                            alignItems: "end",
                            flexDirection: "column",
                            marginTop: { xs: "-20px", sm: "-20px" },
                        }}>
                        <MUIStyled.LightTooltip
                            title={
                                <>
                                    Entity type refers to the legal structure of
                                    a business or organisation. <br />
                                    <br />
                                    Choose from the list of our drop down below.
                                </>
                            }>
                            <MUIStyled.CustomIconButton size="small">
                                <Styled.StyledHelpIcon />
                            </MUIStyled.CustomIconButton>
                        </MUIStyled.LightTooltip>
                        <CustomDropdownField
                            value={formData.companyTypeId}
                            onChange={handleCompanyTypeChange}
                            label="Entity type*"
                            fullWidth
                            variant="outlined"
                            error={companyTypeErrorMsg !== ""}
                            helperText={companyTypeErrorMsg}
                            options={[
                                ...(companyTypesDropdown
                                    ? companyTypesDropdown
                                          .sort((a, b) =>
                                              a.name.localeCompare(b.name)
                                          )
                                          .map((item) => ({
                                              value: String(item.id),
                                              label: item.name,
                                          }))
                                    : []),
                            ]}
                        />
                    </MUIStyled.CustomGrid>

                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{ marginTop: { xs: "15px", sm: "15px" } }}>
                        <CustomTextField
                            value={formData.numberOfEmployees}
                            onChange={handleNumberOfEmployeesChange}
                            label="Number of Employees*"
                            fullWidth
                            variant="outlined"
                            error={noOfEmployeeErrorMsg !== ""}
                            helperText={noOfEmployeeErrorMsg}
                        />
                    </MUIStyled.CustomGrid>

                    {/* Payroll Info Section */}
                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{
                            display: "flex",
                            alignItems: "end",
                            flexDirection: "column",
                            marginTop: { xs: "-20px", sm: "-25px" },
                        }}>
                        {/* Help Icon with Tooltip */}
                        <MUIStyled.LightTooltip
                            title={
                                <>
                                    Payroll frequency is required, as we aim to
                                    match our recurring Tax Invoices to your
                                    Payroll frequency.
                                </>
                            }>
                            <MUIStyled.CustomIconButton size="small">
                                <Styled.StyledHelpIcon />
                            </MUIStyled.CustomIconButton>
                        </MUIStyled.LightTooltip>

                        <CustomDropdownField
                            value={formData.payrollFrequency}
                            onChange={handlePayrollFrequencyChange}
                            label="Payroll Frequency*"
                            fullWidth
                            variant="outlined"
                            error={payrollFrequencyErrorMsg !== ""}
                            helperText={payrollFrequencyErrorMsg}
                            options={[
                                ...(payrollFrequenciesDropdown
                                    ? payrollFrequenciesDropdown.map((day) => ({
                                          value: day,
                                          label: day,
                                      }))
                                    : []),
                            ]}
                        />
                    </MUIStyled.CustomGrid>

                    {formData.payrollFrequency && renderPayrollFields()}

                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{ marginTop: { xs: "5px", sm: "8px" } }}>
                        <CustomDropdownField
                            value={formData.accountingSoftwareID}
                            onChange={handleAccountingSoftwareChange}
                            label="Accounting Software*"
                            fullWidth
                            required
                            variant="outlined"
                            error={accountingSoftwareErrorMsg !== ""}
                            helperText={accountingSoftwareErrorMsg}
                            options={accountingSoftwareOptions}
                        />
                    </MUIStyled.CustomGrid>

                    {isOtherAccountingSoftware(
                        formData.accountingSoftwareID,
                        accountingSoftwareDropdown
                    ) && (
                        <MUIStyled.CustomGrid
                            container
                            size={{ xs: 12, sm: 6 }}
                            spacing={2}
                            sx={{
                                display: "flex",
                                alignItems: "end",
                                flexDirection: "column",
                                marginTop: { xs: "-20px", sm: "-25px" },
                                gap: "0",
                            }}>
                            <MUIStyled.LightTooltip
                                title={
                                    <>
                                        Please write your Accounting Software in
                                        the box below for us to be able to
                                        assist with the specific set up related
                                        to the system you use.
                                    </>
                                }>
                                <MUIStyled.CustomIconButton size="small">
                                    <Styled.StyledHelpIcon />
                                </MUIStyled.CustomIconButton>
                            </MUIStyled.LightTooltip>

                            <CustomTextField
                                value={formData.otherAccountingSoftware}
                                onChange={handleOtherAccountingSoftwareChange}
                                label="Your Account Software*"
                                fullWidth
                                variant="outlined"
                                error={otherAccountingSoftwareMsg !== ""}
                                helperText={otherAccountingSoftwareMsg}
                            />
                        </MUIStyled.CustomGrid>
                    )}
                    <MUIStyled.CustomGrid
                        size={{ xs: 12, sm: 6 }}
                        sx={{
                            marginTop: {
                                xs: "10px",
                                sm: "5px",
                            },
                            marginBottom: { xs: "40px", sm: 0 },
                        }}>
                        <CustomDropdownField
                            value={formData.industryTypeID}
                            onChange={handleIndustryTypeChange}
                            name="industryType"
                            label="Industry Type"
                            fullWidth
                            variant="outlined"
                            options={[
                                ...(industriesDropdown
                                    ? industriesDropdown
                                          .sort((a, b) =>
                                              a.name.localeCompare(b.name)
                                          )
                                          .map((item) => ({
                                              value: String(item.id),
                                              label: item.name,
                                          }))
                                    : []),
                            ]}
                        />
                    </MUIStyled.CustomGrid>
                </MUIStyled.CustomGrid>

                <MUIStyled.CustomGrid
                    sx={{
                        width: "100%",
                        margin: "0 auto",
                        paddingBottom: 5,
                    }}></MUIStyled.CustomGrid>
                <MUIStyled.CustomGrid
                    container
                    xs={10}
                    spacing={4}
                    top="10px"
                    sx={{
                        width: "100%",
                        margin: "0 auto",
                        padding: {
                            xs: "20px",
                            sm: "50px",
                        },
                        paddingBottom: 1,
                        borderRadius: "8px",
                        border: `1px solid ${GlobalTheme.palette.grey.primary.main}`,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.06)",
                        backgroundColor: GlobalTheme.palette.white.primary.main,
                    }}>
                    <DynamicLogoUpload
                        titleText="Upload company logo (optional)"
                        description="Provide your employees with a customised experience on our Steer portal by uploading your company logo."
                        supportedFormatsText="Supported formats: JPG, JPEG, PNG, GIF, WEBP"
                        buttonText="Upload Logo"
                        onImageUploaded={handleImageUploaded}
                        type="logo"
                    />
                </MUIStyled.CustomGrid>

                {companyDetails === "Error" && (
                    <MUIStyled.CustomAlert
                        severity="error"
                        style={{ marginTop: "40px" }}>
                        An internal error occurred, please contact support.
                    </MUIStyled.CustomAlert>
                )}

                {serverError && (
                    <MUIStyled.CustomAlert
                        severity="error"
                        style={{ marginTop: "40px" }}>
                        {serverError}
                    </MUIStyled.CustomAlert>
                )}
                {hasErrorOccurred && errorMessage && (
                    <Styled.ErrorMessage>{errorMessage}</Styled.ErrorMessage>
                )}

                <MUIStyled.CustomGrid
                    container
                    justifyContent={{ xs: "center", sm: "flex-end" }}>
                    <ButtonPurpleNext
                        type="submit"
                        disabled={
                            companyDetails === "Error" || isLoadingS3State
                        }
                        style={{ marginTop: "40px" }}>
                        {isLoading ? (
                            <MUIStyled.CustomCircularProgress size={24} />
                        ) : (
                            "Next"
                        )}
                    </ButtonPurpleNext>
                </MUIStyled.CustomGrid>
            </form>
        </Styled.CompanyFormMainWrapper>
    );
};

export default CompanyDetailForm;
